import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from 'react';
import {
  Box,
  Button,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import '../../index.css';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { AppRow } from '../../layout/appRow';
import { AppButtonOutlined } from '../app/button/appButtonOutlined';
import { AppButtonContained } from '../app/button/appButtonContained';
import { ColorPalette } from '../../styles/constants';
import { AppToolbox } from '../app/appToolbox';
import {
  getTotalHours,
  getTotalHoursDayReach,
  getTotalHoursReach,
} from '../../services/activityService';
import { Activity } from '../../models/activity';
import { TaskAlt } from '@mui/icons-material';
import { ViewMode } from '../../models/preferences';
import useDaysOff from '../../hooks/useDaysOff';
import { useSelector } from 'react-redux';
import { selectUserLocation } from '../../store/profileSlice';
import useAppSnackbar from '../../hooks/useAppSnackbar';
import { AxiosError } from 'axios';
import { addDays, isWeekend } from 'date-fns';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import { AppTooltipContainer } from '../app/appTooltip';
import { AppViewModeSelector } from '../app/appViewModeSelector';
import { useDisplayDate } from '../../hooks/useDisplayDate';
import { useHandleDisplayDate } from '../../hooks/useHandleDisplayDate';
import { getNextDate, getPreviousDate } from '../../services/reportsService';

interface Props {
  handleChangeViewMode: (arg: ViewMode) => void;
  setDateActivity: Dispatch<SetStateAction<Date>>;
  setIsOpenExport: Dispatch<SetStateAction<boolean>>;
  onOpenAddActivity: (date: Date | undefined) => void;
  viewMode: ViewMode;
  dateActivity: Date;
  activities: Activity[];
  isAddActivityButtonVisible: boolean;
  employeeWorkingHours: number;
}

const makeStyles = (theme: Theme) => ({
  buttonArrow: {
    color: ColorPalette.DARK_GRAY,
    minWidth: '40px',
  },

  appRowSecond: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'unset',
    },
  },

  appRowThird: {
    justifyContent: 'flex-start',
  },

  typoWarning: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
  },

  iconView: {
    mx: 1.5,
  },

  buttonView: {
    mr: 2,
  },

  typoView: {
    mr: 2,
  },

  buttonExport: {
    mr: 2,
  },

  buttonDate: {
    pt: 1.5,
  },

  loggedHoursText: {
    fontFamily: 'Nexa',
    fontSize: '18px',
    fontWeight: 700,
    color: ColorPalette.WARNING_RED,
    display: 'flex',
    alignItems: 'center',
    ml: 2,
  },
  bankHolidayText: {
    fontFamily: 'Nexa',
    fontSize: '18px',
    fontWeight: 700,
    color: ColorPalette.DARK_GRAY,
    display: 'flex',
    alignItems: 'center',
  },

  umbrellaIcon: {
    ml: 0.5,
    fontSize: '1.2rem',
    color: ColorPalette.DARK_GRAY,
  },
  tooltip: {
    bgcolor: ColorPalette.LIGHT_GRAY,
    '& .MuiTooltip-arrow': {
      color: ColorPalette.LIGHT_GRAY,
    },
    pl: 2,
    pr: 2,
    pt: 1,
    pb: 1,
  },
  bankHolidayBox: { display: 'flex', alignItems: 'center' },
  warning: { color: ColorPalette.WARNING_RED },
});

export function ActivityToolbox({
  viewMode,
  handleChangeViewMode,
  activities = [],
  dateActivity,
  isAddActivityButtonVisible,
  setDateActivity,
  onOpenAddActivity,
  setIsOpenExport,
  employeeWorkingHours,
}: Props): JSX.Element {
  const [isOpenViewMode, setIsOpenViewMode] = useState<boolean>(true);
  const userLocation = useSelector(selectUserLocation);

  const theme = useTheme();
  const style = makeStyles(theme);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { displayDate } = useDisplayDate({ viewMode, dateActivity, isMobile });

  const handleOpenViewMode = useCallback(() => {
    setIsOpenViewMode(isOpenViewMode ? false : true);
  }, [isOpenViewMode]);

  const handleExport = useCallback(() => {
    setIsOpenExport(true);
  }, [setIsOpenExport]);

  const handlePreviousDate = useCallback(() => {
    setDateActivity(getPreviousDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const handleNextDate = useCallback(() => {
    setDateActivity(getNextDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const displayTitle = useMemo(() => {
    switch (viewMode) {
      case ViewMode.DAY:
        return 'Daily';
      case ViewMode.MONTH:
        return 'Monthly';
      case ViewMode.OLD_VIEW:
        return 'Table';
      default:
        return 'Weekly';
    }
  }, [viewMode]);

  const totalHoursWorked = useMemo(
    () => getTotalHours(activities),

    [activities]
  );

  const { onErrorSnackbar } = useAppSnackbar();

  const { targetHolidays, dayOffError, getTargetHolidays } = useDaysOff({
    targetDate: dateActivity,
    country: userLocation,
    mode: viewMode,
  });

  useEffect(() => {
    if (dayOffError) {
      onErrorSnackbar(dayOffError as AxiosError);
    }
  }, [dayOffError, onErrorSnackbar]);

  const shouldDisplayCurrentDate = useMemo(() => {
    return viewMode === ViewMode.MONTH || viewMode === ViewMode.OLD_VIEW;
  }, [viewMode]);

  const totalHoursReach = useMemo(() => {
    switch (viewMode) {
      case ViewMode.DAY:
        return getTotalHoursDayReach(
          dateActivity,
          targetHolidays,
          getTargetHolidays(addDays(dateActivity, 1)),
          userLocation,
          employeeWorkingHours
        );
      case ViewMode.OLD_VIEW:
      case ViewMode.MONTH:
        return getTotalHoursReach(
          dateActivity,
          targetHolidays,
          userLocation,
          'month',
          employeeWorkingHours
        );

      default:
        return getTotalHoursReach(
          dateActivity,
          targetHolidays,
          userLocation,
          'week',
          employeeWorkingHours
        );
    }
  }, [viewMode, dateActivity, targetHolidays, userLocation, getTargetHolidays, employeeWorkingHours]);

  const { handleDisplayDate } = useHandleDisplayDate({
    viewMode,
    handleChangeViewMode,
  });

  const bankHolidayMark = useMemo(
    () => (
      <Box sx={style.bankHolidayBox}>
        <Typography sx={style.bankHolidayText}>Bank holiday</Typography>
        <BeachAccessOutlinedIcon sx={style.umbrellaIcon} />
      </Box>
    ),
    [style.bankHolidayBox, style.bankHolidayText, style.umbrellaIcon]
  );

  const dateStyle =
    viewMode === ViewMode.DAY &&
    (targetHolidays?.length || isWeekend(dateActivity))
      ? style.warning
      : {};

  const loggedHours = useMemo(() => {
    const isActivities = !!activities.length;
    const isHolidays = !!targetHolidays?.length;

    if (viewMode !== ViewMode.DAY) {
      return (
        <Typography
          sx={style.loggedHoursText}
        >{`Logged: ${totalHoursWorked}/${totalHoursReach}h`}</Typography>
      );
    }

    if (isHolidays) {
      return (
        <Box sx={{ display: 'flex' }}>
          <AppTooltipContainer
            content={bankHolidayMark}
            title={targetHolidays[0].name}
          />

          {isActivities && (
            <Typography
              sx={style.loggedHoursText}
            >{`Logged: ${totalHoursWorked}/${totalHoursReach}h`}</Typography>
          )}
        </Box>
      );
    }

    if (!isActivities) {
      return '';
    }

    return (
      <Typography
        sx={style.loggedHoursText}
      >{`Logged: ${totalHoursWorked}/${totalHoursReach}h`}</Typography>
    );
  }, [
    activities.length,
    targetHolidays,
    viewMode,
    bankHolidayMark,
    style.loggedHoursText,
    totalHoursWorked,
    totalHoursReach,
  ]);

  return (
    <AppToolbox
      pageTitle={`${displayTitle} activity log `}
      firstChildren={
        <>
          <IconButton sx={style.iconView} onClick={handleOpenViewMode}>
            <ViewModuleOutlinedIcon />
          </IconButton>
          {isOpenViewMode && (
            <AppViewModeSelector
              handleChangeViewMode={handleChangeViewMode}
              viewMode={viewMode}
            />
          )}
        </>
      }
      secondChildren={
        <>
          <AppRow>
            <AppButtonOutlined
              handleClick={handleExport}
              sx={style.buttonExport}
              label="Export"
            />
            {isAddActivityButtonVisible && (
              <AppButtonContained
                label="Activity"
                isIconButton
                handleClick={() =>
                  onOpenAddActivity(
                    shouldDisplayCurrentDate ? new Date() : undefined
                  )
                }
              />
            )}
          </AppRow>
        </>
      }
      thirdChildren={
        <>
          <AppRow sx={style.appRowSecond}>
            <AppRow sx={style.appRowThird}>
              <Button sx={style.buttonArrow} onClick={handlePreviousDate}>
                <ArrowBackIosRoundedIcon
                  fontSize={isMobile ? 'small' : 'medium'}
                />
              </Button>
              <Button sx={style.buttonArrow} onClick={handleNextDate}>
                <ArrowForwardIosRoundedIcon
                  fontSize={isMobile ? 'small' : 'medium'}
                />
              </Button>
              <Button sx={style.buttonDate} onClick={handleDisplayDate}>
                <Typography variant="h1" sx={dateStyle}>
                  {displayDate}
                </Typography>
              </Button>
            </AppRow>

            {totalHoursWorked === totalHoursReach ? (
              <TaskAlt color="primary" sx={style.typoWarning} />
            ) : (
              <Typography sx={style.typoWarning} variant="warningOne">
                {loggedHours}
              </Typography>
            )}
          </AppRow>
        </>
      }
    />
  );
}
